import { Button, Stack, Typography } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { styled } from '@mui/material/styles';
import { ME_QUERY } from '~/graphql/queries/me';
import { useToast } from '~/shared/contexts/ToastContext';

/**
 * Stripe Checkout `success_url` (not proof of payment). Webhooks finalize retainer approval; refetch cached data.
 */
const RetainerFundingSuccessPage = () => {
  const apolloClient = useApolloClient();
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;

    async function syncAfterFundingSuccess() {
      try {
        await apolloClient.refetchQueries({
          include: 'active',
        });
        await apolloClient.query({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        Sentry.captureException(error, {
          tags: { feature: 'retainer_funding_success_sync' },
        });
        showToast(
          'Could not refresh your data. Your payment may still be processing—open the matter to see the latest status.',
          'error',
        );
      }
    }

    void syncAfterFundingSuccess();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, showToast]);

  return (
    <Root spacing={2}>
      <PageTitle variant="h6">Payment submitted</PageTitle>
      <Body variant="body2" color="text.secondary">
        If your payment succeeded, your retainer will move to approved shortly.
        This page can load before our systems finish processing—check the matter
        for the latest status.
      </Body>
      <Button
        component={RouterLink}
        to="/matters"
        variant="contained"
        fullWidth
      >
        Back to matters
      </Button>
    </Root>
  );
};

const Root = styled(Stack)(({ theme }) => ({
  maxWidth: 480,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}));

const Body = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export default RetainerFundingSuccessPage;
